<template src="./MyTickets.html"></template>

<script>
import { mapGetters } from 'vuex'
import { getAllTickets } from '../../services'
import { ticketPriorityVal } from '../../utils/constants'
import { TicketModuleAccess } from '../../utils/roles'
import { daysUntilDue, dueUrgency, isOpenStatus, shiftDayKey, todayKey } from '../../utils/dueDate'
import TicketCard from '../../components/Ticket/TicketCard.vue'
import TicketDetailDialog from '../../components/Ticket/TicketDetailDialog.vue'

// XAN-193 (adjustments) — "Mis pendientes": what each user owns across their projects.
//
// Everything on the screen is derived from the user's tickets, loaded once (every page, no cap).
// Counters, sections, search and filters are computed here rather than asked from the API, which
// keeps them in step with each other by construction (a counter can never disagree with the list
// under it), makes every filter instant, and adds nothing to the backend.
//
// This screen is a summary, not the full list: each section shows its first few tickets and hands
// off to the board, which is where the whole set is browsed, with the equivalent filters applied.

const VIEWS = Object.freeze([
  { key: 'open', label: 'Pendientes', icon: 'mdi-format-list-checks', tone: 'neutral' },
  { key: 'overdue', label: 'Vencidos', icon: 'mdi-alert-circle-outline', tone: 'danger' },
  { key: 'today', label: 'Vencen hoy', icon: 'mdi-clock-outline', tone: 'warning' },
  { key: 'upcoming', label: 'Próximos', icon: 'mdi-calendar-blank-outline', tone: 'neutral' },
  { key: 'done', label: 'Completados', icon: 'mdi-check-circle-outline', tone: 'success' }
])

const SECTION_TITLES = Object.freeze({
  overdue: 'Vencidos',
  today: 'Vencen hoy',
  upcoming: 'Próximos',
  done: 'Completados'
})

const EMPTY_BY_VIEW = Object.freeze({
  open: { icon: 'mdi-check-all', title: 'Estás al día', text: 'No tienes tickets pendientes.' },
  overdue: { icon: 'mdi-check-decagram-outline', title: 'Nada vencido', text: 'Todos tus tickets están dentro de su fecha.' },
  today: { icon: 'mdi-calendar-check-outline', title: 'Nada vence hoy', text: '' },
  upcoming: { icon: 'mdi-calendar-blank-outline', title: 'Sin tickets próximos', text: '' },
  done: { icon: 'mdi-check-circle-outline', title: 'Aún no has completado tickets', text: '' }
})

const URGENT_KEYS = Object.freeze(['overdue', 'today'])

// Six fits evenly in the one-, two- and three-column layouts a laptop or phone lands on, so a
// capped section never ends in a row with a single orphan card.
const SECTION_LIMIT = 6

const PRIORITY_RANK = Object.freeze({ URGENT: 4, HIGH: 3, MEDIUM: 2, LOW: 1, NO_PRIORITY: 0 })

// Accent- and case-insensitive, so "gestion" finds "Gestión".
const normalize = (text) => (text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

const byPriority = (a, b) => (PRIORITY_RANK[b.priority] || 0) - (PRIORITY_RANK[a.priority] || 0)

export default {
  name: 'MyTickets',
  components: { TicketCard, TicketDetailDialog },
  data: () => ({
    initialized: false,
    loading: true,
    refreshing: false,
    tickets: [],
    // Captured on every fetch and shared by all cards, so "Vence hoy" / "Venció ayer" are measured
    // against one instant and change together.
    now: new Date(),
    activeView: 'open',
    search: '',
    projectId: null,
    priority: null,
    views: VIEWS,
    priorityOptions: ticketPriorityVal,
    detailDialogOpen: false,
    selectedTicketId: null
  }),
  computed: {
    ...mapGetters({
      user: 'getUser'
    }),
    hasToolbarFilters () {
      return !!(this.search || this.projectId || this.priority)
    },
    // Only projects that actually have a ticket of mine, so the dropdown never offers an empty result.
    projectOptions () {
      const byId = new Map()
      this.tickets.forEach((ticket) => {
        if (ticket.project) byId.set(ticket.project.id, ticket.project)
      })
      return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'es'))
    },
    // Search, project and priority narrow the data first; the counters are then computed over what
    // is left, so they always describe the list the user is actually looking at.
    filteredTickets () {
      const query = normalize(this.search)
      return this.tickets.filter((ticket) => {
        if (this.projectId && ticket.project?.id !== this.projectId) return false
        if (this.priority && ticket.priority !== this.priority) return false
        if (query && !normalize(`${ticket.title} ${ticket.project?.name || ''}`).includes(query)) return false
        return true
      })
    },
    groups () {
      const groups = { overdue: [], today: [], upcoming: [], done: [] }
      this.filteredTickets.forEach((ticket) => {
        if (ticket.status === 'DONE') {
          groups.done.push(ticket)
          return
        }
        if (!isOpenStatus(ticket.status)) return
        const urgency = dueUrgency(ticket, this.now)
        // A ticket without a due date can never be late, so it waits with the upcoming work.
        groups[urgency === 'undated' ? 'upcoming' : urgency].push(ticket)
      })

      const days = (ticket) => daysUntilDue(ticket.dueDate, this.now)
      groups.overdue.sort((a, b) => days(a) - days(b) || byPriority(a, b))
      groups.today.sort(byPriority)
      groups.upcoming.sort((a, b) => {
        if (days(a) === null && days(b) === null) return byPriority(a, b)
        if (days(a) === null) return 1
        if (days(b) === null) return -1
        return days(a) - days(b) || byPriority(a, b)
      })
      groups.done.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
      return groups
    },
    counts () {
      const { overdue, today, upcoming, done } = this.groups
      return {
        open: overdue.length + today.length + upcoming.length,
        overdue: overdue.length,
        today: today.length,
        upcoming: upcoming.length,
        done: done.length
      }
    },
    sections () {
      const keys = this.activeView === 'open' ? ['overdue', 'today', 'upcoming'] : [this.activeView]
      return keys
        .map((key) => {
          const tickets = this.groups[key]
          return {
            key,
            title: SECTION_TITLES[key],
            total: tickets.length,
            tickets: tickets.slice(0, SECTION_LIMIT),
            boardLink: tickets.length > SECTION_LIMIT ? this.boardLinkFor(key) : null
          }
        })
        .filter((section) => section.total)
    },
    urgentSections () {
      return this.sections.filter((section) => URGENT_KEYS.includes(section.key))
    },
    otherSections () {
      return this.sections.filter((section) => !URGENT_KEYS.includes(section.key))
    },
    emptyState () {
      if (this.hasToolbarFilters) {
        return { icon: 'mdi-magnify-close', title: 'Ningún ticket coincide con los filtros', text: '' }
      }
      return EMPTY_BY_VIEW[this.activeView]
    }
  },
  watch: {
    // `user` is populated asynchronously by Vuex once Firebase resolves the auth state, so on a
    // hard reload this component can mount before it is ready. Same guard as Projects.vue.
    user: {
      immediate: true,
      handler (user) {
        if (!user || this.initialized) return
        this.initialized = true

        if (!TicketModuleAccess()(user.rol)) {
          this.$router.replace('/')
          return
        }
        this.fetchMyTickets()
      }
    }
  },
  methods: {
    // `silent` keeps the current content on screen and shows a thin progress bar instead of the
    // skeleton, so refreshing after an edit does not make the whole page blink.
    async fetchMyTickets ({ silent = false } = {}) {
      if (silent) this.refreshing = true
      else this.loading = true
      try {
        const data = await getAllTickets({ assignedToId: this.user.id })
        this.tickets = data.filter((ticket) => ticket.status !== 'CANCELLED')
        this.now = new Date()
      } catch (err) {
        console.error('Error fetching my tickets:', err)
        if (!silent) this.tickets = []
      } finally {
        this.loading = false
        this.refreshing = false
      }
    },
    /**
     * Board URL for "see everything in this section": the current user's tickets narrowed the same
     * way the section is, plus the project and priority picked here. Text search has no equivalent
     * on the board, so it is not carried over.
     *
     * The board groups by status and cannot express "open only", so its result can include a closed
     * ticket whose date matches (in the "Completado" column). That is why the link reads "Ver todos
     * en el tablero" and does not promise the section's exact count.
     */
    boardLinkFor (key) {
      const today = todayKey(this.now)
      const query = { onlyMine: '1' }
      if (key === 'overdue') query.dueDateTo = shiftDayKey(today, -1)
      if (key === 'today') Object.assign(query, { dueDateFrom: today, dueDateTo: today })
      if (key === 'upcoming') query.dueDateFrom = shiftDayKey(today, 1)
      if (key === 'done') query.status = 'DONE'
      if (this.projectId) query.projectId = this.projectId
      if (this.priority) query.priority = this.priority
      return { path: '/tickets', query }
    },
    clearFilters () {
      this.search = ''
      this.projectId = null
      this.priority = null
    },
    // "3" when everything fits; "6 de 14" once the section is capped, so the cut is never silent.
    sectionCountLabel (section) {
      return section.total > section.tickets.length ? `${section.tickets.length} de ${section.total}` : section.total
    },
    openDetail (ticket) {
      this.selectedTicketId = ticket.id
      this.detailDialogOpen = true
    }
  }
}
</script>

<style lang="scss" src="./MyTickets.scss" scoped></style>
